import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslate } from 'react-admin'
import { Divider, makeStyles, MenuItem, Tooltip } from '@material-ui/core'
import LinkIcon from '@material-ui/icons/Link'
import { useMyLayout } from '../../context/useMyLayout'
import ConditionalRender from '../ConditionalRender/ConditionalRender'
import GoBackButton from '../GoBackButton/GoBackButton'
import useMenuItemLinks from '../../hooks/useMenuItemLinks'
import useHistoryPush from '../../hooks/useHistoryPush'
import menuItemLinksStyles from './menuItemLinks.styles'
import createMenuItemLinkStyle from '../../utils/helpers/createMenuItemLinkStyle'
import useQueryParams from '../../hooks/useQueryParams'
import localStorageHelper from '../../utils/helpers/localStorageHelper'
import localStorageIds from '../../utils/constants/localStorageIds'
import ORGANISATION_TYPES from '../../utils/constants/OrganisationTypes'
import isStatistician from '../../utils/helpers/isStatistician'

const MenuItemLinks = ({ myOrgs }) => {
  const [visible, setVisible] = useState(false)
  const { menuChoice, teams, error, singleOrg } = useMyLayout()
  const openSidebar = useSelector((state) => state.admin.ui.sidebarOpen)
  const translate = useTranslate()
  const { redirect } = useHistoryPush()
  const { team, org } = useQueryParams()
  const hasOrgs = myOrgs && myOrgs.length > 0

  // Get all sidebar options logic from the hook
  const {
    selectedSidebarOption,
    sidebarOptions,
    isProfileItems,
    isTeamSettings,
    getMenuItemClickHandler,
    getSelectedItemName,
    getItemText,
    isMenuItemDisabled,
    shouldDisableTooltipHover,
  } = useMenuItemLinks(menuChoice, hasOrgs)

  const { setLocalStorageValue } = localStorageHelper(localStorageIds.SELECTED_TEAM)
  const useStyles = makeStyles(menuItemLinksStyles)
  const classes = useStyles()

  // In order to show "Back to organisation" after an amount of time.
  useEffect(() => {
    if (openSidebar) {
      setTimeout(() => {
        setVisible(true)
      }, 100)
    }
    setVisible(false)
  }, [openSidebar])

  const visibleBackButton = visible && (isProfileItems || isTeamSettings) && !isStatistician()

  const handleGoBackButtonClick = () => {
    if (singleOrg?.type === ORGANISATION_TYPES.ORG_COMPETITION || singleOrg?.type === ORGANISATION_TYPES.ORG_FEDERATION) {
      redirect('/calendar')
    } else if (team) {
      redirect('/teamCalendar')
    } else if (teams?.length === 1) {
      setLocalStorageValue(teams[0])
      redirect('/teamCalendar', [{ team: teams[0].id }])
    } else if (org) {
      redirect('/calendar')
    } else {
      redirect('/')
    }
  }

  const selectedItemName = getSelectedItemName()

  return (
    <>
      <ConditionalRender renderValue={visibleBackButton}>
        <GoBackButton label={translate('ra.text.backToOrg')} goTo={handleGoBackButtonClick} />
      </ConditionalRender>

      {selectedSidebarOption &&
        sidebarOptions.map((item) => (
          <React.Fragment key={item.name}>
            {item.connectedFrom && <Divider className={classes.divider} />}
            {item.connectedFrom && openSidebar && (
              <Tooltip
                title={`Shared with your team through its connection to a ${item.connectedFrom} team`}
                placement='right'
                arrow
              >
                <div className={classes.sectionLabel}>
                  <LinkIcon />
                  <span className={classes.itemText}>{item.connectedFrom}</span>
                </div>
              </Tooltip>
            )}
            <Tooltip
              title={
                !error
                  ? translate('ra.text.unauthorizedTooltip')
                  : translate('ra.errorMessages.connectionError')
              }
              disableHoverListener={shouldDisableTooltipHover(item, error)}
              placement='right'
              arrow
            >
              <div>
                <MenuItem
                  key={item.name}
                  onClick={getMenuItemClickHandler(item)}
                  style={createMenuItemLinkStyle(selectedItemName, item.name)}
                  disabled={isMenuItemDisabled(item, error)}
                >
                  <div className={classes.textWrapper}>
                    {selectedItemName === item.name ? item.iconSelected : item.icon}
                    <span className={classes.itemText} title={item.fullText}>{getItemText(item)}</span>
                  </div>
                </MenuItem>
              </div>
            </Tooltip>
          </React.Fragment>
        ))}
    </>
  )
}

export default MenuItemLinks
